import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  input,
  Output,
  signal,
  Signal,
  viewChild,
} from '@angular/core';

import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ButtonTheme, IconPosition, NotificationScale, NotificationTheme } from '@fcom/ui-components';
import { findFirstFocusableElement } from '@fcom/ui-components/utils/dom.utils';
import { Amount, ServiceCatalogService } from '@fcom/dapi/interfaces';
import { Category, FinnairPassengerItem } from '@fcom/dapi/api/models';

import { NonSeatBundleAvailability } from '../../../../../store';
import { overlappingSelectionsForCategorySelector } from '../../../../../store/selectors';
import { getPassengerTierLevel, getServiceOverlapStateForPaxForFlight } from '../../../utils';
import { ServiceOverlapResolution, ServiceOverlaps } from '../../../interfaces';

/**
 * One passenger's offer for an ancillary+ancillary (non-seat) bundle: the passenger name and
 * "Add combo" action, the bundle's constituent service cards and the passenger-specific price. When the
 * bundle isn't available for this passenger on this flight (`availability` null/unavailable), the
 * passenger is still listed but the offer is replaced by a short "not available" notice.
 */
@Component({
  selector: 'fin-bundle-offer-card',
  templateUrl: './bundle-offer-card.component.html',
  styleUrls: ['./bundle-offer-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BundleOfferCardComponent {
  private store$ = inject(Store);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private injector = inject(Injector);

  readonly constituents = input.required<ServiceCatalogService[]>();
  readonly passenger = input.required<FinnairPassengerItem>();
  readonly availability = input.required<NonSeatBundleAvailability | null>();
  readonly selectedForPassenger = input.required<boolean>();
  readonly isBundleAncillariesIncludedInTier = input<boolean>(false);
  readonly category = input.required<Category>();
  readonly fragmentId = input.required<string>();

  @Output() addBundle = new EventEmitter<void>();
  @Output() removeBundle = new EventEmitter<void>();

  private passengerHeading = viewChild.required<ElementRef<HTMLElement>>('passengerHeading');
  private overlapResolution = signal<ServiceOverlapResolution | undefined>(undefined);

  private readonly serviceOverlapsForCategory = computed(() =>
    this.store$.selectSignal(overlappingSelectionsForCategorySelector(this.category()))()
  );

  readonly serviceOverlaps: Signal<ServiceOverlaps> = computed(() => {
    const resolution = this.overlapResolution();

    return getServiceOverlapStateForPaxForFlight(
      this.serviceOverlapsForCategory(),
      this.fragmentId(),
      this.passenger().id,
      resolution
    );
  });

  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationScale = NotificationScale;

  readonly passengerName = computed(() =>
    [this.passenger()?.firstName, this.passenger()?.lastName].filter(Boolean).join(' ')
  );
  readonly tierLevel = computed(() => getPassengerTierLevel(this.passenger().id, [this.passenger()]));

  /**
   * A zero price in the combo's currency, shown when the combo is free via tier benefit. The currency is
   * taken from the constituent catalog services (or another passenger's combo price) so it is not hardcoded.
   */
  readonly freePrice = computed<Amount | null>(() => {
    const currencyCode =
      this.constituents()?.[0]?.totalPrice?.currencyCode ?? this.availability()?.bundleTotalPrice?.currencyCode;
    return currencyCode ? { currencyCode, amount: '0' } : null;
  });

  continueToSelection(): void {
    this.overlapResolution.set('discard');
    this.addBundle.emit();
    afterNextRender(() => findFirstFocusableElement(this.elementRef.nativeElement)?.focus(), {
      injector: this.injector,
    });
  }

  keepExistingSelection(): void {
    this.overlapResolution.set('keep');
    afterNextRender(() => this.passengerHeading().nativeElement.focus(), { injector: this.injector });
  }
}
